import { useEffect } from "react";
import useGet from "../../common/useGet";
import {
  API_ACTIVE_PARTNERS,
  API_SETTLEMENT_BENEFICIARIES,
} from "../../../utils/apis";
import { IFormSection, TFormHookParams } from "../../../types/common";
import {
  BENEFICIARY_TYPES,
  BOOLEAN_SELECTIONS,
} from "../../../utils/constants";
import {
  createActivateAction,
  createColumn,
  createDeactivateAction,
  createViewEdit,
  formatDateTime,
  getForm,
  getRegister,
  returnObject,
} from "../../../utils/helpers";
import { URL_SETTLEMENT_BENEFICIARIES } from "../../../utils/urls";
import { FormActions } from "../../../components/common/FormActions";
import { BooleanTags } from "../../../components/common/registerUtils";

const url = URL_SETTLEMENT_BENEFICIARIES;

export const useSettlementBeneficiaries = () => {
  const tableColumns = () => [
    createColumn("name", true, "Name"),
    createColumn("beneficiary_type", true, "Beneficiary Type"),
    createColumn("is_active", true, "Active"),
    createColumn("created_by", true, "Created By"),
    createColumn("created_at", true, "Created At"),
    createColumn("actions", false),
  ];

  const tableData = (data: any[] = []) => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => {
      return {
        ...item,
        is_active: <BooleanTags status={item?.is_active} />,
        created_by: item?.created_by?.name,
        created_at: formatDateTime(item?.created_at),
        actions: (
          <FormActions
            actions={[
              ...createViewEdit(url, item?.id),
              ...returnObject(item?.is_active, [
                createDeactivateAction(
                  `${API_SETTLEMENT_BENEFICIARIES}${item?.id}/deactivate/`,
                  item?.name,
                  {},
                  {
                    method: "POST",
                  },
                ),
              ]),
              ...returnObject(!item?.is_active, [
                createActivateAction(
                  `${API_SETTLEMENT_BENEFICIARIES}${item?.id}/activate/`,
                  item?.name,
                  {},
                  {
                    method: "POST",
                  },
                ),
              ]),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Settlement Beneficiary Details",
          fields: {
            beneficiary_type: item?.beneficiary_type,
            name: item?.name,
            bank_name: item?.bank_name,
            account_number: item?.account_number,
            account_name: item?.account_name,
            is_active: <BooleanTags status={item?.is_active} />,
            created_at: formatDateTime(item?.created_at),
            updated_at: formatDateTime(item?.updated_at),
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
  };
};

export const useSettlementBeneficiariesForm = ({
  form,
  updateFormValues,
}: TFormHookParams) => {
  const {
    data: partners,
    loading: partnersLoading,
    error: partnersError,
    loadData: loadPartners,
  } = useGet();

  useEffect(() => {
    loadPartners({ api: API_ACTIVE_PARTNERS });
  }, []);

  useEffect(() => {
    if (form?.code && partners?.partners) {
      const selectedPartner = partners.partners.find(
        (partner: any) => partner.partner_code === form.code,
      );
      if (
        selectedPartner &&
        updateFormValues &&
        form?.name !== selectedPartner.partner_name
      ) {
        updateFormValues({ name: selectedPartner.partner_name });
      }
    }
  }, [form?.code, form?.name, partners?.partners]);

  const sections: IFormSection[] = [
    {
      title: "Settlement Beneficiary",
      fields: [
        {
          field_name: "beneficiary_type",
          label: "Beneficiary Type",
          type: "select",
          options_data: BENEFICIARY_TYPES.map((item: any) => ({
            id: item.id,
            name: item.name,
          })),
          props: { required: true, placeholder: "Select Beneficiary Type" },
        },
        {
          field_name: "code",
          label: "Beneficiary",
          type: "select",
          options_data: partners?.partners?.map((item: any) => ({
            id: item.partner_code,
            name: item.partner_name,
          })),
          props: { required: true, placeholder: "Select Beneficiary Name" },
        },
        {
          field_name: "name",
          label: "Beneficiary Name",
          type: "text",
          visible: false,
          props: {
            required: true,
            disabled: true,
            value: partners?.partners?.find(
              (partner: any) => partner.partner_code === form?.code,
            )?.partner_name,
          },
        },
        {
          field_name: "bank_name",
          label: "Bank Name",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "account_number",
          label: "Account Number",
          type: "number",
          props: { required: true },
        },
        {
          field_name: "account_name",
          label: "Account Name",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "is_active",
          label: "Is Active",
          type: "select",
          options_data: BOOLEAN_SELECTIONS,
          props: { required: true },
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  function onSubmit(payload: any, id: string | number) {
    const cleanedPayload: any = {};

    // Fields that should be kept in the payload
    const validFields = [
      "beneficiary_type",
      "code",
      "name",
      "bank_name",
      "account_name",
      "account_number",
      "is_active",
    ];

    Object.keys(payload).forEach((key) => {
      if (validFields.includes(key)) {
        cleanedPayload[key] = payload[key];
      }
    });

    // Convert account_number to string
    if (cleanedPayload.account_number) {
      cleanedPayload.account_number = String(cleanedPayload.account_number);
    }

    return {
      id,
      api: API_SETTLEMENT_BENEFICIARIES,
      params: cleanedPayload,
    };
  }

  return {
    sections,
    partnersLoading,
    partnersError,
    redirect,
    onSubmit,
    defaultValues: {
      is_active: true,
    },
  };
};
